import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetIngredientPricesQuery, useGetIngredientsCodesQuery } from '../../features/ordersApiSlice';
import { useLocation } from 'react-router';
import Logo from '../../assets/images/pdf/riise-pdf-logo.jpg';
import QRCode from 'react-qr-code';
import {
  CONSTANTS,
  EXTRA_UNITS,
  kcal_per_g_carbs,
  kj_per_g_carbs,
  MICRO_INGREDIENT_IDS,
} from '../../Data/OrdersPdf';
import { ORDER_TRANSLATIONS } from '../../translations/ordersPdf';
import { declaredDaysForOrderMonths, pouchWeightMg } from '../../utils/labelDays';
import Img1 from '../../assets/images/pdf/how-to-take-1.jpg';
import Img2 from '../../assets/images/pdf/how-to-take-2.jpg';
import Img3 from '../../assets/images/pdf/how-to-take-3.jpg';
import LabelIconsImg from '../../assets/images/pdf/label-ingredients-4x6.png';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getBestBeforeDate } from '../../utils';

const ExternalOrderLabelIngredients4x6 = () => {
  const location = useLocation();
  const { order } = location.state || {};

  const [lang, setLang] = useState('en');
  const orderDetailsRef = useRef();

  useEffect(() => {
    if (!order) return;
    setLang(['de', 'germany'].includes(order?.user?.country?.toLowerCase()) ? 'de' : 'en');
  }, [order]);

  const { data: ingredientPrices, isLoading: ingredientPricesLoading } = useGetIngredientPricesQuery(lang);
  const { data: ingredientCodes, isLoading: ingredientCodesLoading } = useGetIngredientsCodesQuery();
  const isLoading = ingredientPricesLoading || ingredientCodesLoading;

  const TRANSlATIONS = useMemo(() => ORDER_TRANSLATIONS[lang], [lang]);
  const pageRef = useRef();

  const downloadPdf = async (refs, orderId) => {
    // 4x6 inches in mm: 4 inches = 101.6mm, 6 inches = 152.4mm
    const pdf = new jsPDF('p', 'mm', [98.5, 149]);
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    for (let i = 0; i < refs.length; i++) {
      const canvas = await html2canvas(refs[i].current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/jpeg', 1.0);

      // Calculate dimensions to fit the 4x6 page
      const imgProps = {
        width: pageWidth,
        height: (canvas.height * pageWidth) / canvas.width,
      };

      // If height exceeds page height, scale down to fit
      if (imgProps.height > pageHeight) {
        imgProps.height = pageHeight;
        imgProps.width = (canvas.width * pageHeight) / canvas.height;
      }

      if (i !== 0) pdf.addPage([101.6, 152.4]);
      pdf.addImage(imgData, 'JPEG', 0, 0, imgProps.width, imgProps.height);
    }

    pdf.save(`riise_label_4x6_${orderId}.pdf`);
  };

  const totalWeight = useMemo(() => {
    let sum = 0;
    order?.ingredients?.forEach((element) => {
      const prices = ingredientPrices?.data?.find(
        (item) => item?.ingredient_id === parseInt(element?.ingredientId),
      );
      const active = parseFloat(prices?.active_percentage);

      const hydroSum = (parseFloat(element?.quantity) * parseFloat(prices?.hydro_percentage)) / active;
      const saccharoseSum =
        (parseFloat(element?.quantity) * parseFloat(prices?.saccharose_percentage)) / active;
      const starchSum = (parseFloat(element?.quantity) * parseFloat(prices?.starch_percentage)) / active;
      const restSum =
        (parseFloat(element?.quantity) * parseFloat(prices?.rest_ingredients_percentage)) / active;

      const allSum = parseFloat(element?.quantity) + hydroSum + saccharoseSum + starchSum + restSum;

      sum += allSum;
    });
    // SHI-528 — declared supply (30/month), not the produced count (31/month), and built
    // from the FILLED pouch: neutral pellets are in the bag and consumed. See utils/labelDays.
    const total = pouchWeightMg(sum) * declaredDaysForOrderMonths(order);
    return Math.round(total / 1000);
  }, [order, ingredientPrices]);

  const bestBeforeDate = useMemo(() => getBestBeforeDate(order), [order]);

  const totalSaccharose = useMemo(() => {
    let sum = 0;
    order?.ingredients?.forEach((element) => {
      const price = ingredientPrices?.data?.find(
        (item) => item?.ingredient_id === parseInt(element?.ingredientId),
      );
      if (price) {
        const sumOfPrices =
          (parseFloat(price?.saccharose_percentage) * parseFloat(element?.quantity)) /
          parseFloat(price?.active_percentage);
        sum += sumOfPrices;
      }
    });
    return sum;
  }, [order]);

  const totalStarch = useMemo(() => {
    let sum = 0;
    order?.ingredients?.forEach((element) => {
      const price = ingredientPrices?.data?.find(
        (item) => item?.ingredient_id === parseInt(element?.ingredientId),
      );
      if (price) {
        sum +=
          (parseFloat(price?.starch_percentage) * parseFloat(element?.quantity)) /
          parseFloat(price?.active_percentage);
      }
    });
    return sum;
  }, [order]);

  const carbohydrate = useMemo(
    () => Math.round((totalSaccharose + totalStarch) / 1000),
    [totalSaccharose, totalStarch],
  );

  const carbohydratePercentage = useMemo(() => {
    const percentage = carbohydrate / CONSTANTS['Carbohydrates'];
    return percentage < 1 ? '<1%' : percentage.toFixed(2) + '%';
  }, [carbohydrate]);

  const sugar = useMemo(() => Math.round(totalSaccharose / 1000), [totalSaccharose]);

  const sugarPercentage = useMemo(() => {
    const percentage = sugar / CONSTANTS['Sugars'];
    return percentage < 1 ? '<1%' : percentage.toFixed(2) + '%';
  }, [sugar]);

  const energy = useMemo(
    () => kcal_per_g_carbs * carbohydrate + '/' + kj_per_g_carbs * carbohydrate,
    [carbohydrate],
  );

  const energyPercentage = useMemo(() => {
    const energyPerc = (kcal_per_g_carbs * carbohydrate) / CONSTANTS['Enerygy'];
    return energyPerc < 1 ? '<1%' : energyPerc.toFixed(2) + '%';
  }, [carbohydrate]);

  const productName = useMemo(() => {
    if (order?.user?.source_company_name?.includes('gettested')) {
      return 'Get Tested Custom supplements';
    } else if (order?.user?.source_company_name?.includes('biovariance')) {
      return 'bioVariant - Personalized';
    }
    return `Riise for you`;
  }, [order]);

  const ingredients = useMemo(
    () =>
      ingredientPrices?.data?.filter((item) =>
        order?.ingredients?.some((orderIng) => parseInt(orderIng.ingredientId) === item.ingredient_id),
      ) || [],
    [ingredientPrices?.data, order?.ingredients],
  );
  console.log('ingredientPrices?.data? => ', order?.ingredients);

  const ingredientLabelString = useMemo(
    () =>
      ingredients
        ?.map((item) => `${item?.ingredient?.ingredient} (${item?.ingredient?.scientific_name})`)
        .filter(Boolean)
        .join(', '),
    [ingredients],
  );

  console.log('ingredientLabelString => ', ingredientLabelString);

  const renderIngredientsSection = () => {
    const halfLength = Math.ceil((order?.ingredients?.length || 0) / 2) + 2;
    const leftColumn = order?.ingredients?.slice(0, halfLength) || [];
    const rightColumn = order?.ingredients?.slice(halfLength) || [];

    const renderIngredientLine = (item) => {
      const ingredientData = ingredientCodes?.data?.find(
        (code) => code?.code === parseInt(item?.ingredientId),
      );
      const dose = MICRO_INGREDIENT_IDS?.includes(item?.ingredientId)
        ? (parseFloat(item?.quantity) * 1000).toFixed(2)
        : item?.quantity;
      const unit = MICRO_INGREDIENT_IDS?.includes(item?.ingredientId) ? ' µg' : ' mg';
      const quantity = parseFloat(item?.quantity);
      const euNrv = parseFloat(ingredientData?.eu_nrv);
      const extraUnit = Object.keys(EXTRA_UNITS)?.includes(item?.ingredientId?.toString())
        ? ' ' + EXTRA_UNITS[item?.ingredientId]
        : '';
      const nrv = euNrv <= 0 || isNaN(euNrv) ? '*' : parseInt((quantity / euNrv) * 100) + '%';

      return (
        <div key={item.id} className="t-flex t-items-end t-justify-between">
          <div className="t-flex-1">
            <span className="t-text-[20px] t-font-semibold" style={{ lineHeight: '20px' }}>
              {item?.ingredientName}
            </span>
          </div>

          <div className="t-flex t-items-center t-justify-center t-gap-2">
            <span
              className="t-min-w-[80px] t-text-[20px] t-font-semibold t-text-right"
              style={{ lineHeight: '20px' }}
            >
              {dose}
              {unit}
              {extraUnit}
            </span>
            <span
              className="t-min-w-[70px] t-text-right t-text-[20px] t-font-semibold"
              style={{ lineHeight: '20px' }}
            >
              {nrv}
            </span>
          </div>
        </div>
      );
    };

    const renderColumnHeader = () => (
      <div className="t-border-t t-border-b t-border-black t-py-2 mb-2">
        <h3 className="t-text-xl t-font-bold t-m-0">{TRANSlATIONS.nutritionalValues}</h3>
        <p className="t-text-xl t-font-semibold t-m-0">{TRANSlATIONS.nutrientAmountNrv}</p>
      </div>
    );

    return (
      <div className="t-border-b t-border-black t-pb-4">
        <div className="t-flex t-gap-10">
          <div className="t-flex-1">
            {renderColumnHeader()}
            <div className="t-font-mono t-text-xs t-leading-relaxed">
              {leftColumn.map((item) => renderIngredientLine(item))}
            </div>
          </div>
          <div className="t-flex-1">
            {renderColumnHeader()}
            <div className="t-font-mono t-text-xs t-leading-relaxed">
              {rightColumn.map((item) => renderIngredientLine(item))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNutritionTable = () => {
    const nutritionData = [
      { label: TRANSlATIONS.energy, value: `${energy} kcal/kj`, percentage: energyPercentage },
      { label: TRANSlATIONS.fat, value: '0 g', percentage: '0%' },
      { label: TRANSlATIONS.saturatedFattyAcids, value: '0 g', percentage: '0%' },
      { label: TRANSlATIONS.carbohydrates, value: `${carbohydrate} g`, percentage: carbohydratePercentage },
      { label: TRANSlATIONS.sugar, value: `${sugar} g`, percentage: sugarPercentage },
      { label: TRANSlATIONS.protein, value: '0 g', percentage: '0%' },
      { label: TRANSlATIONS.salt, value: '0 g', percentage: '0%' },
    ];

    return (
      <div className="t-flex t-gap-6 t-items-start t-justify-between">
        {/* Left side - Icons */}
        <div className="t-flex-shrink-0 t-mt-12">
          <img src={LabelIconsImg} alt="Label Icons" className="t-w-40" />
        </div>

        {/* Right side - Nutrition Table */}
        <div className="t-flex-1 t-max-w-[350px] t-border-b t-border-black t-pb-4">
          <div className="t-border-b t-border-black t-py-2">
            <h3 className="t-text-xl t-font-bold t-m-0 t-text-right">{TRANSlATIONS.nutritionalInfo}</h3>
          </div>
          <div className="t-font-mono t-text-xs t-leading-relaxed t-text-lg">
            {nutritionData.map((item, index) => (
              <div key={index} className="t-flex t-items-center ">
                <p
                  className="t-flex-1 t-text-[20px] t-font-semibold t-leading-0"
                  style={{ lineHeight: '20px' }}
                >
                  {item.label}
                </p>
                <p
                  className="t-flex t-items-center t-justify-between t-gap-2 t-text-[20px] t-font-semibold"
                  style={{ lineHeight: '20px' }}
                >
                  <span className="t-min-w-[100px] t-text-center">{item.value}</span>
                  <span className="t-min-w-[50px] t-text-right">{item.percentage}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }
  return (
    <>
      <button onClick={() => downloadPdf([pageRef], order?.internal_order_id)}>Download</button>
      <div className="t-w-[794px] t-mx-auto t-flex t-flex-col t-bg-white" ref={orderDetailsRef}>
        <div className="t-relative t-flex t-flex-col t-gap-1 t-p-3" ref={pageRef}>
          <div className="t-flex t-items-center t-justify-between t-font-bold">
            <p className="t-text-lg">
              {productName} - {order?.user?.full_name}
            </p>
            <p className="t-text-lg">
              {TRANSlATIONS.netQuantity}: {totalWeight ?? 0}g
            </p>
          </div>
          <p className="t-text-[20px] t-font-semibold t-pb-4" style={{ lineHeight: '20px' }}>
            {TRANSlATIONS.ingredients}: {ingredientLabelString}, {TRANSlATIONS.ingredientsDesc}
          </p>
          {renderIngredientsSection()}
          {renderNutritionTable()}
          <div className="t-flex t-items-center t-justify-between t-font-bold">
            <p className="t-text-lg">
              {TRANSlATIONS.batchNumber}: {order?.internal_order_id}
            </p>
            <p className="t-text-lg">
              {TRANSlATIONS.bestBeforeDate}: {bestBeforeDate}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExternalOrderLabelIngredients4x6;
